import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, useNavigate, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import format from 'date-fns/format';
import { addDays } from 'date-fns';

// Component imports
import TeamMembers from './TeamMembers';
import TeamAvailability from './TeamAvailability';
import TeamAvailabilitySettings from './TeamAvailabilitySettings';
import MyAvailability from './MyAvailability';
import Properties from './Properties';
import EditProperty from './EditProperty';
import CreateProperty from './CreateProperty';
import ViewProperty from './ViewProperty';
import ArchivedProperties from './ArchivedProperties';
import Settings from './Settings';
import Reservations from './Reservations';
import Products from './Products';
import CreateProduct from './CreateProduct';
import EditProduct from './EditProduct';
import Footer from '../components/Footer';
import Assignments from './Assignments';
import Schedule from './Schedule';
import MySchedule from './MySchedule';
import CompletedJobs from './CompletedJobs';
import Navigation from '../components/Navigation';
import Header from '../components/Header';
import Inspections from './Inspections';
import WorkOrders from './WorkOrders';
import PastWorkOrders from './PastWorkOrders';
import AllStatusWorkOrders from './AllStatusWorkOrders';
import LostAndFound from './LostAndFound';
import WhereToGo from './WhereToGo';
import JobDetails from './JobDetails';
import Orders from './Orders';
import OrdersHistory from './OrdersHistory';
import CreateWorkOrder from './CreateWorkOrder';
import EditWorkOrder from './EditWorkOrder';
import WorkOrderDetail from './WorkOrderDetail';
import LostAndFoundArchive from './LostAndFoundArchive';
import CreateLostFound from './CreateLostFound';
import LostFoundDetail from './LostFoundDetail';
import CreateWhereToGo from './CreateWhereToGo';
import EditWhereToGo from './EditWhereToGo';
import WhereToGoDetail from './WhereToGoDetail';
import WhereToGoCategory from './WhereToGoCategory';
import UserSettings from './UserSettings';
import AppInvites from './AppInvites';
import AppInviteSettings from './AppInviteSettings';
import NotesWidget from '../components/NotesWidget';
import JobsToAssignWidget from '../components/dashboard/jobs-to-assign-widget';
import WorkOrderWidget from '../components/dashboard/work-order-widget';
import MyWeekScheduleWidget from '../components/dashboard/my-week-schedule-widget';
import TeamStatusWidget from '../components/dashboard/team-status-widget';
import TeamAvailabilityStatusWidget from '../components/dashboard/team-availability-status-widget';
import TimeClockWidget from '../components/dashboard/time-clock-widget';
import { useMyPrivileges } from '../hooks/useMyPrivileges';

import RecurringJobs from './RecurringJobs';
import CanceledJobs from './CanceledJobs';
import Reports from './Reports';
import HourlyPayroll from './HourlyPayroll';
import PresetExpenses from './PresetExpenses';
import GPSTracking from './GPSTracking';
import QuickBooksAssignments from './QuickBooksAssignments';
import TimeClock from './TimeClock';
import PropertyPerformance from './PropertyPerformance';
import PropertyPerformanceDetail from './PropertyPerformanceDetail';
import HelpDocumentation from './HelpDocumentation';
import HelpTutorials from './HelpTutorials';
import HelpDevelopers from './HelpDevelopers';
import HelpArticle from './HelpArticle';

const TEAM_MEMBER_VIEWER_ROLES = [
  'ADMIN',
  'GUEST_APP_MANAGER',
  'PROPERTY_MANAGER',
  'HOUSEKEEPER_MANAGER',
  'INSPECTOR',
  'MAINTENANCE',
  'CALL_CENTER',
  'USER',
  'MANAGER'
];

const GET_STREAMLINE_STATUS = gql`
  query GetStreamlineStatus {
    getStreamlineStatus {
      isConnected
      lastCheck
      message
    }
  }
`;

// Add new query for user assigned jobs
const GET_USER_ASSIGNED_JOBS = gql`
  query GetUserAssignedJobs($input: JobsQueryInput!) {
    jobs(input: $input) {
      items {
        id
        status
        scheduledDate
        property {
          id
          name
        }
      }
      total
    }
  }
`;



// New component for role-based routes
function RoleBasedRoute({ element, allowedRoles }) {
  const { user } = useAuth();
  
  
  // Check if the user's role is in the allowed roles list
  const hasPermission = user && allowedRoles.includes(user.role);
  
  return hasPermission ? element : <Navigate to="/dashboard" />;
}

function PrivilegeRoute({ element, privilegeKey }) {
  const { hasPrivilege, loading } = useMyPrivileges();

  if (loading) return null;
  return hasPrivilege(privilegeKey) ? element : <Navigate to="/dashboard" replace />;
}

function TimeClockRoute() {
  const { user } = useAuth();
  const canViewTimeClock = user?.role === 'ADMIN' || Boolean(user?.requiresTimeClock);

  return canViewTimeClock ? <TimeClock /> : <Navigate to="/dashboard" />;
}

export default function Dashboard() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout, isLoggingOut } = useAuth();
  const navigate = useNavigate();

  const { data: streamlineData, loading: streamlineLoading } = useQuery(GET_STREAMLINE_STATUS, {
    pollInterval: 0, // Disable polling - only check on page load
    fetchPolicy: 'cache-first', // Use cache to avoid repeated API calls
    skip: !user || isLoggingOut, // Skip query during logout
  });



  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Sidebar */}
      <aside className={`sidebar fixed inset-y-0 left-0 bg-sky-950 transition-transform duration-200 z-50 transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 flex flex-col w-[280px]`}>
        {/* Mobile user info section */}
        <div className="lg:hidden p-4 border-b border-sky-900">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-sky-800 overflow-hidden" style={{ aspectRatio: '1 / 1' }}>
                <img src="https://placehold.co/40x40" alt="Profile" className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="text-white font-medium">{user?.name || 'User'}</div>
                <span className="bg-sky-600 text-white text-xs px-2 py-1 rounded-full">{user?.role || 'User'}</span>
              </div>
            </div>
            
            {/* Close button */}
            <button 
              onClick={toggleMobileMenu}
              className="p-2 text-gray-400 hover:text-white rounded-lg"
              aria-label="Close menu"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Logo section - Fixed at top */}
        <div className="h-16 flex-shrink-0 flex items-center px-6 border-b border-sky-900 justify-end">
          <img src={process.env.PUBLIC_URL + '/images/logo.png'} alt="Logo" className="h-8" />
        </div>

        {/* Navigation - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <Navigation toggleMobileMenu={toggleMobileMenu} />
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-[280px] flex flex-col min-h-screen bg-sky-950 lg:bg-gray-100">
        <div className="sticky top-0 z-40 lg:static lg:z-auto">
          <Header toggleMobileMenu={toggleMobileMenu} handleLogout={handleLogout} />
        </div>

        {/* Main Content Area */}
        <main className="flex-1 px-2 md:px-4 lg:px-8 py-2 md:py-4">
          <Routes>
            <Route path="/" element={<DashboardHome />} />
            <Route
              path="/team-members"
              element={
                <RoleBasedRoute
                  element={<TeamMembers />}
                  allowedRoles={TEAM_MEMBER_VIEWER_ROLES}
                />
              }
            />
            <Route 
              path="/team-availability" 
              element={
                <PrivilegeRoute
                  element={<TeamAvailability />}
                  privilegeKey="TEAM_AVAILABILITY"
                />
              } 
            />
            <Route
              path="/team-availability/settings"
              element={
                <RoleBasedRoute
                  element={<TeamAvailabilitySettings />}
                  allowedRoles={['ADMIN']}
                />
              }
            />
            <Route 
              path="/properties" 
              element={
                <RoleBasedRoute 
                  element={<Properties />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER', 'PROPERTY_MANAGER', 'HOUSEKEEPER_MANAGER', 'MAINTENANCE', 'CALL_CENTER']} 
                />
              } 
            />
            <Route 
              path="/properties/create" 
              element={
                <RoleBasedRoute 
                  element={<CreateProperty />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER', 'PROPERTY_MANAGER', 'HOUSEKEEPER_MANAGER']} 
                />
              } 
            />
            <Route 
              path="/properties/edit/:id" 
              element={
                <RoleBasedRoute 
                  element={<EditProperty />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER', 'PROPERTY_MANAGER', 'HOUSEKEEPER_MANAGER']} 
                />
              } 
            />
            <Route 
              path="/properties/view/:id" 
              element={
                <RoleBasedRoute 
                  element={<ViewProperty />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER', 'PROPERTY_MANAGER', 'HOUSEKEEPER_MANAGER', 'MAINTENANCE', 'CALL_CENTER']} 
                />
              } 
            />
            <Route 
              path="/properties/archived" 
              element={
                <RoleBasedRoute 
                  element={<ArchivedProperties />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER', 'PROPERTY_MANAGER', 'HOUSEKEEPER_MANAGER']} 
                />
              } 
            />
            
            {/* Settings Route - Protected from PROPERTY_MANAGER and HOUSEKEEPER_MANAGER */}
            <Route 
              path="/settings" 
              element={
                <RoleBasedRoute 
                  element={<Settings />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER']} 
                />
              } 
            />
            
            <Route path="/user-settings" element={<UserSettings />} />
            <Route
              path="/my-availability"
              element={
                <PrivilegeRoute
                  element={<MyAvailability />}
                  privilegeKey="TEAM_AVAILABILITY"
                />
              }
            />
            <Route 
              path="/reservations" 
              element={
                <RoleBasedRoute 
                  element={<Reservations />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER', 'PROPERTY_MANAGER', 'HOUSEKEEPER_MANAGER', 'CALL_CENTER']} 
                />
              } 
            />
            
            {/* Updated Assignments route to use the RoleBasedRoute component with enum values */}
            <Route 
              path="/assignments" 
              element={
                <RoleBasedRoute 
                  element={<Assignments />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER', 'PROPERTY_MANAGER', 'HOUSEKEEPER_MANAGER']} 
                />
              } 
            />
            
            <Route
              path="/schedule"
              element={
                <RoleBasedRoute
                  element={<Schedule />}
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER', 'PROPERTY_MANAGER', 'HOUSEKEEPER_MANAGER', 'CALL_CENTER']}
                />
              }
            />
            <Route
              path="/canceled-jobs"
              element={
                <RoleBasedRoute
                  element={<CanceledJobs />}
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER', 'PROPERTY_MANAGER', 'HOUSEKEEPER_MANAGER']}
                />
              }
            />
            <Route path="/my-schedule" element={<MySchedule />} />
            <Route path="/time-clock" element={<TimeClockRoute />} />
            <Route path="/completed-jobs" element={<CompletedJobs />} />
            
            {/* Recurring Jobs Route - Protected for admin and manager roles */}
            <Route 
              path="/recurring-jobs" 
              element={
                <RoleBasedRoute 
                  element={<RecurringJobs />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER', 'PROPERTY_MANAGER']} 
                />
              } 
            />
            
            {/* Guest App Routes - Protected from PROPERTY_MANAGER and HOUSEKEEPER_MANAGER */}
            <Route 
              path="/products" 
              element={
                <RoleBasedRoute 
                  element={<Products />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER']} 
                />
              } 
            />
            <Route 
              path="/products/new" 
              element={
                <RoleBasedRoute 
                  element={<CreateProduct />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER']} 
                />
              } 
            />
            <Route 
              path="/products/:id/edit" 
              element={
                <RoleBasedRoute 
                  element={<EditProduct />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER']} 
                />
              } 
            />
            
            <Route path="/inspections" element={<Inspections />} />
            <Route path="/work-orders" element={<WorkOrders />} />
            <Route path="/work-orders/new" element={<CreateWorkOrder />} />
            <Route path="/work-orders/:id" element={<WorkOrderDetail />} />
            <Route path="/work-orders/:id/edit" element={<EditWorkOrder />} />
            <Route path="/lost-found" element={<LostAndFound />} />
            <Route path="/lost-found/create" element={<CreateLostFound />} />
            <Route path="/lost-found/:id" element={<LostFoundDetail />} />
            <Route path="/lost-found-archive" element={<LostAndFoundArchive />} />
            
            {/* Where to Go Routes - Protected from PROPERTY_MANAGER and HOUSEKEEPER_MANAGER */}
            <Route 
              path="/where-to-go" 
              element={
                <RoleBasedRoute 
                  element={<WhereToGo />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER']} 
                />
              } 
            />
            <Route 
              path="/where-to-go/new" 
              element={
                <RoleBasedRoute 
                  element={<CreateWhereToGo />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER']} 
                />
              } 
            />
            <Route 
              path="/where-to-go/:id" 
              element={
                <RoleBasedRoute 
                  element={<WhereToGoDetail />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER']} 
                />
              } 
            />
            <Route 
              path="/where-to-go/:id/edit" 
              element={
                <RoleBasedRoute 
                  element={<EditWhereToGo />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER']} 
                />
              } 
            />
            <Route 
              path="/where-to-go/category/new" 
              element={
                <RoleBasedRoute 
                  element={<WhereToGoCategory />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER']} 
                />
              } 
            />
            <Route 
              path="/where-to-go/category/:id" 
              element={
                <RoleBasedRoute 
                  element={<WhereToGoCategory />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER']} 
                />
              } 
            />
            
            <Route path="/jobs/:jobId" element={<JobDetails />} />
            
            {/* Orders Routes */}
            <Route 
              path="/orders" 
              element={
                <RoleBasedRoute 
                  element={<Orders />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER']} 
                />
              } 
            />
            <Route 
              path="/orders/history" 
              element={
                <RoleBasedRoute 
                  element={<OrdersHistory />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER']} 
                />
              } 
            />
            
            {/* App Invites Routes */}
            <Route 
              path="/invites" 
              element={
                <RoleBasedRoute 
                  element={<AppInvites />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER']} 
                />
              } 
            />
            <Route 
              path="/app-invite-settings" 
              element={
                <RoleBasedRoute 
                  element={<AppInviteSettings />} 
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'MANAGER']} 
                />
              } 
            />
            
            <Route path="/past-work-orders" element={<PastWorkOrders />} />
            <Route path="/work-orders-all-status" element={<AllStatusWorkOrders />} />
            <Route 
              path="/reports" 
              element={
                <RoleBasedRoute 
                  element={<Reports key="general-reports" />}
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER']}
                />
              } 
            />
            <Route 
              path="/reports/:reportId" 
              element={
                <RoleBasedRoute 
                  element={<Reports key="general-reports" />}
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'PROPERTY_MANAGER']} 
                />
              } 
            />
            <Route
              path="/reports/work-orders"
              element={
                <RoleBasedRoute
                  element={<Reports key="work-order-reports" section="work-orders" />}
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'PROPERTY_MANAGER']}
                />
              }
            />
            <Route
              path="/reports/work-orders/:reportId"
              element={
                <RoleBasedRoute
                  element={<Reports key="work-order-reports" section="work-orders" />}
                  allowedRoles={['ADMIN', 'GUEST_APP_MANAGER', 'PROPERTY_MANAGER']}
                />
              }
            />
            <Route
              path="/reports/property-performance"
              element={
                <RoleBasedRoute
                  element={<PropertyPerformance />}
                  allowedRoles={['ADMIN', 'PROPERTY_MANAGER']}
                />
              }
            />
            <Route
              path="/reports/property-performance/:propertyId"
              element={
                <RoleBasedRoute
                  element={<PropertyPerformanceDetail />}
                  allowedRoles={['ADMIN', 'PROPERTY_MANAGER']}
                />
              }
            />
            <Route
              path="/hourly-payroll/print"
              element={
                <RoleBasedRoute
                  element={<HourlyPayroll printView />}
                  allowedRoles={['ADMIN']}
                />
              }
            />
            <Route
              path="/hourly-payroll"
              element={
                <RoleBasedRoute
                  element={<HourlyPayroll />}
                  allowedRoles={['ADMIN']}
                />
              }
            />
            <Route 
              path="/gps-tracking" 
              element={
                <RoleBasedRoute 
                  element={<GPSTracking />} 
                  allowedRoles={['ADMIN', 'PROPERTY_MANAGER', 'HOUSEKEEPER_MANAGER', 'CALL_CENTER']}
                />
              } 
            />
            <Route 
              path="/preset-expenses" 
              element={
                <RoleBasedRoute 
                  element={<PresetExpenses />} 
                  allowedRoles={['ADMIN', 'MANAGER']} 
                />
              } 
            />
            <Route path="/help/documentation" element={<HelpDocumentation />} />
            <Route path="/help/documentation/:slug" element={<HelpArticle />} />
            <Route path="/help/tutorials" element={<HelpTutorials />} />
            <Route path="/help/tutorials/:slug" element={<HelpArticle />} />
            <Route
              path="/help/developers"
              element={
                <RoleBasedRoute
                  element={<HelpDevelopers />}
                  allowedRoles={['ADMIN']}
                />
              }
            />
            <Route
              path="/help/developers/:slug"
              element={
                <RoleBasedRoute
                  element={<HelpArticle />}
                  allowedRoles={['ADMIN']}
                />
              }
            />
            <Route 
              path="/quickbooks-assignments" 
              element={
                <RoleBasedRoute 
                  element={<QuickBooksAssignments />} 
                  allowedRoles={['ADMIN', 'PROPERTY_MANAGER']} 
                />
              } 
            />
          </Routes>
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
}

// DashboardHome component
const DashboardHome = () => {
  const { user, isLoggingOut } = useAuth();
  const { hasPrivilege } = useMyPrivileges();
  const canAccessTeamAvailability = hasPrivilege('TEAM_AVAILABILITY');
  

  const isHousekeeper = user?.role === 'HOUSEKEEPER';
  const isAdmin = user?.role === 'ADMIN';
  const isPropertyManager = user?.role === 'PROPERTY_MANAGER';
  const isHousekeeperManager = user?.role === 'HOUSEKEEPER_MANAGER';
  const isInspector = user?.role === 'INSPECTOR';
  const isCallCenter = user?.role === 'CALL_CENTER';
  
  
  // Check if user can see assignment-related widgets and data
  const canSeeAssignmentData = isAdmin || isPropertyManager || isHousekeeperManager;
  
  // Replace static today date with state to allow navigation between days
  const [selectedDate, setSelectedDate] = useState(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  });
  
  // Date change handler for widget
  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  // Query for user's assigned jobs
  const { data: userJobsData, loading: userJobsLoading } = useQuery(GET_USER_ASSIGNED_JOBS, {
    variables: { 
      input: {
        assignedToUserId: parseInt(user?.id, 10) || 0,
        status: "PENDING",
        limit: 100,
        // Use format matching other components, looking for jobs within next 30 days
        startDate: format(new Date(), 'yyyy-MM-dd') + 'T00:00:00.000Z',
        endDate: format(addDays(new Date(), 30), 'yyyy-MM-dd') + 'T23:59:59.999Z'
      }
    },
    skip: !user?.id,
    fetchPolicy: 'network-only',
    
    onCompleted: (data) => {
      // Query completed successfully
    },
    
    onError: (error) => {
      // Query failed
    }
  });






  // Get user's assigned jobs count
  const assignedJobsCount = userJobsData?.jobs?.total || 0;
  const hasAssignedJobs = assignedJobsCount > 0;

  // Inspector: fetch jobs ready for inspection assigned to them
  const { data: inspectorReadyData, loading: inspectorReadyLoading } = useQuery(GET_USER_ASSIGNED_JOBS, {
    variables: {
      input: {
        assignedToUserId: parseInt(user?.id || 0, 10),
        status: 'READY_FOR_INSPECTION',
        limit: 100,
        startDate: format(new Date(), 'yyyy-MM-dd') + 'T00:00:00.000Z',
        endDate: format(addDays(new Date(), 30), 'yyyy-MM-dd') + 'T23:59:59.999Z'
      }
    },
    skip: !isInspector,
    fetchPolicy: 'network-only'
  });

  const readyInspectionCount = inspectorReadyData?.jobs?.total || 0;

  // Jobs to Complete widget component
  const JobsToCompleteWidget = () => (
    <Link to="/dashboard/my-schedule" className="block bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div className="bg-amber-100 p-3 rounded-lg">
          <svg className="w-6 h-6 text-amber-600" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" 
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" 
                  fill="none" />
            <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" 
                  strokeLinejoin="round" />
            <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" 
                  strokeLinejoin="round" />
            <path d="M4.5 7.5L7.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" 
                  strokeLinejoin="round" />
            <path d="M16.5 4.5L19.5 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" 
                  strokeLinejoin="round" />
          </svg>
        </div>
        <span className="text-2xl font-bold">
          {userJobsLoading ? '...' : assignedJobsCount}
        </span>
      </div>
      <div className="mt-2 text-gray-600">Jobs to Complete</div>
      <div className="mt-2">
        <span className="inline-flex items-center px-3 py-1.5 border border-amber-600 text-xs font-medium rounded-md text-amber-600 bg-white">
          View Jobs
          <svg className="w-3 h-3 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </span>
      </div>
    </Link>
  );

  // Ready for Inspection widget (Inspector only)
  const JobsReadyForInspectionWidget = () => (
    <Link to="/dashboard/my-schedule#ready-for-inspection" className="block bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div className="bg-indigo-100 p-3 rounded-lg">
          <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 8a6 6 0 11-12 0 6 6 0 0112 0zM12 14a10 10 0 00-9 5.5A1 1 0 004 21h16a1 1 0 00.9-1.45A10 10 0 0012 14z" />
          </svg>
        </div>
        <span className="text-2xl font-bold">
          {inspectorReadyLoading ? '...' : readyInspectionCount}
        </span>
      </div>
      <div className="mt-2 text-gray-600">Jobs Ready for Inspection</div>
      <div className="mt-2">
        <span className="inline-flex items-center px-3 py-1.5 border border-indigo-600 text-xs font-medium rounded-md text-indigo-600 bg-white">
          View Jobs
          <svg className="w-3 h-3 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </span>
      </div>
    </Link>
  );


  return (
    <div className="space-y-6">
      <TimeClockWidget user={user} />

      {/* Team Member Status - personal check-in widget */}
      <TeamStatusWidget user={user} isLoggingOut={isLoggingOut} />

      {/* Date Navigation - Only show for users who can see assignment data */}
      <JobsToAssignWidget 
        selectedDate={selectedDate}
        canSeeAssignmentData={canSeeAssignmentData}
        onDateChange={handleDateChange}
        showDateNavigation={true}
        showUnassignedWidget={false}
        user={user}
      />
      
      {/* Updated Grid - 2 columns on mobile, 2 on tablet, 4 on desktop */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Jobs to Complete Widget - Show for any user with assigned jobs */}
        {hasAssignedJobs && <JobsToCompleteWidget />}
        
        {/* Unassigned Jobs Widget - In the grid with other widgets */}
        <JobsToAssignWidget 
          selectedDate={selectedDate}
          canSeeAssignmentData={canSeeAssignmentData}
          onDateChange={handleDateChange}
          showDateNavigation={false}
          showUnassignedWidget={true}
          user={user}
        />
        
        {/* Properties - Call Center only */}
        {isCallCenter && (
          <Link 
            to="/dashboard/properties" 
            className="block bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div className="bg-sky-100 p-3 rounded-lg">
                <svg className="w-6 h-6 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
            <div className="mt-2 text-gray-600">Properties</div>
            <div className="mt-2">
              <span className="inline-flex items-center px-3 py-1.5 border border-sky-600 text-xs font-medium rounded-md text-sky-600 bg-white">
                Go to Properties
                <svg className="w-3 h-3 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </span>
            </div>
          </Link>
        )}
        {/* Reservations - Call Center only */}
        {isCallCenter && (
          <Link
            to="/dashboard/reservations"
            className="block bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div className="bg-indigo-100 p-3 rounded-lg">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <div className="mt-2 text-gray-600">Reservations</div>
            <div className="mt-2">
              <span className="inline-flex items-center px-3 py-1.5 border border-indigo-600 text-xs font-medium rounded-md text-indigo-600 bg-white">
                Go to Reservations
                <svg className="w-3 h-3 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </span>
            </div>
          </Link>
        )}
        {/* Work Order Widgets */}
        <WorkOrderWidget 
          user={user}
          isHousekeeper={isHousekeeper}
          isLoggingOut={isLoggingOut}
          isInspector={isInspector}
          isCallCenter={isCallCenter}
        />
      </div>
      
      {/* Notes and daily team availability */}
      <div className={`grid grid-cols-1 gap-6 mt-6 ${canAccessTeamAvailability ? 'lg:grid-cols-2' : ''}`}>
        {/* Notes Widget - Takes 50% of available space */}
        <div className="col-span-1">
          <NotesWidget isAdmin={isAdmin} userRole={user?.role} isLoggingOut={isLoggingOut} />
        </div>
        
        {/* Right column for today's team availability */}
        {canAccessTeamAvailability && (
          <div className="col-span-1 space-y-6">
            <TeamAvailabilityStatusWidget user={user} isLoggingOut={isLoggingOut} />
          </div>
        )}
      </div>

      {/* This Week's Schedule Widget */}
      {canAccessTeamAvailability && <MyWeekScheduleWidget />}
    </div>
  );
}
